/**
 * ATL-913 — out-of-stock enforcement for the New Sale checkout.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * `sellOutOfStockProducts` is enforced ONLY on the client: neither `saveNewSale`
 * (global/Firebase/sales.ts) nor the backend `create_sale_atomic` RPC knows the role, and both
 * apply an unconditional `increment(-quantity)`. The checkout screen is therefore the single
 * place the permission is decided, and it must decide it against stock that is CURRENT.
 *
 * The defect (customer ИП Смак): the pre-commit check read `cartItem.storeStock`, a copy frozen
 * into the cart line by `addNewCartItem` (`{...product}`) at the moment the product was tapped.
 * Nothing re-syncs cart lines with the live `products` listener afterwards, so a cart built while
 * stock was 31 still claimed 31 six minutes later, after another till had sold 30. A 4-unit sale
 * passed the guard and drove stock to −3. The composite branch two lines below read
 * `productsDict[...]` — live — which is why only plain product lines were affected.
 *
 * The fix is to read the live product doc from the products dict (fed by the Firestore
 * `onSnapshot` listener in AppStack) and fall back to the cart line's own snapshot only when the
 * product is no longer in the catalog — which is exactly what the write path does for its refs.
 *
 * WHAT THIS DOES NOT DO
 * ---------------------
 * It is not atomic. It shrinks the window from "the lifetime of the cart" (minutes) to "the
 * products listener's replication lag" (sub-second); two tills committing in the same instant can
 * still both pass. Closing that needs a conditional write, and a Firestore transaction is
 * deliberately NOT an option for `saveNewSale` — transactions fail offline, and the checkout is
 * built to keep selling offline (see the asymmetry note in global/Firebase/stockWriteRegistry.ts).
 * The real atomic guard belongs in the backend once reads are cut over to Supabase; today a
 * backend refusal would arrive AFTER the Firestore batch committed and would only split the books.
 *
 * A SEPARATE, DELIBERATELY UNCHANGED GAP: a product with no `storeStock` entry for the store
 * (never supplied there) reads as `undefined`, and `undefined < quantity` is false — so it sells
 * freely. `comparableNumber` below preserves that exact behaviour rather than tightening it,
 * because the add-to-cart guard is permissive in the same way and flipping both would start
 * refusing every product an org never registered stock for. Tracked separately; not part of this
 * fix.
 *
 * WHAT IT DOES CHECK, LINE FOR LINE, IS THE CART'S TOTAL DRAW ON EACH PRODUCT — not each line in
 * isolation. `saveNewSale` emits one `increment(-quantity)` PER LINE (sales.ts), and the same
 * product legitimately occupies several lines: with `mergeSimilarItems` off every tap is its own
 * line, and a modified line forces the next tap into a new one regardless of that setting. A
 * per-line check therefore passed two 1-unit lines against 1 unit of stock and committed −1 — the
 * same below-zero sale ATL-913 is about, reached by a different route. Requirements are summed per
 * product id across plain lines AND across the ingredients every at-sale composite consumes, which
 * is exactly the set of decrements the write path will issue.
 */

import { isPseudoLine } from '../global/Firebase/cartLineType'

/** The subset of a role this decision needs. */
export interface StockCheckRole {
  /**
   * The Firestore role id ('admin', 'seller', …) in BOTH read modes — see `canSellOutOfStock`.
   * Never the Supabase UUID: the backend transform already resolves it (roles.service.ts) and
   * AppStack normalises it again (AppStack.js) for exactly this reason.
   */
  id?: string | null
  /** The spelling the API-shaped role carries alongside `id` (backend `transformRole`). */
  firestoreId?: string | null
  /** The raw-row spelling. No current read path produces it; accepted defensively. */
  firestore_id?: string | null
  permissions?: Record<string, boolean | undefined> | null
}

/** The subset of a product / cart line this decision needs. */
export interface StockCheckProduct {
  id?: string | null
  name?: string | null
  storeStock?: Record<string, number> | null
  isComposite?: boolean | null
  manufacturedBeforeSale?: boolean | null
  ingredients?: Array<{ id: string, quantity: number }> | null
}

export interface StockCheckCartLine extends StockCheckProduct {
  quantity: number
}

export type StockCheckProductsDict = Record<string, StockCheckProduct | undefined> | null | undefined

/** The first line that cannot be sold, described well enough to build the user-facing message. */
export interface InsufficientStockLine {
  kind: 'product' | 'ingredient'
  productName?: string | null
  ingredientName?: string | null
  /**
   * ADS-FIX-2026-003 — the product whose stock ran short: the line's own product, or for an
   * at-sale composite the INGREDIENT. Absent only for a line that carries no id.
   */
  shortProductId?: string | null
  /** The stock the guard compared against (live catalog), exactly as compared. */
  available: number
  /** The cart's total draw on that product at the point it went short — what was attempted. */
  attempted: number
}

export interface FindInsufficientStockLineArgs {
  cartItems: StockCheckCartLine[] | null | undefined
  productsDict: StockCheckProductsDict
  store: string
  userRole: StockCheckRole | null | undefined
}

/**
 * Whether this role may sell past zero.
 *
 * `id === 'admin'` is the load-bearing clause and it holds in BOTH read modes — the earlier claim
 * that Supabase reads leave the role's `id` as a UUID does not survive the code it names. The
 * backend's `transformRole` returns `id: role.firestore_id || role.id` (roles.service.ts), the
 * `/settings` payload the constants fetch reads is that same transform (settings.service.ts), and
 * AppStack normalises it once more on the way into `rolesDict` — "set id to firestoreId so
 * existing admin checks work" (AppStack.js). An admin's role object therefore says `id: 'admin'`
 * whichever store it came from, and the pre-ATL-913 add-to-cart guard was never refusing admins.
 *
 * The two id-spelling clauses below are belt-and-braces for a role object that reaches this
 * function WITHOUT that normalisation: `firestoreId` is what an API-shaped role carries next to
 * `id`, `firestore_id` what a raw Supabase row would. Both are checked because guessing which one
 * a future read path emits is exactly how the bypass would fail silently — and an admin refused
 * mid-shift is as bad a failure as a cashier who is not refused. (`isAdminAtom` checks only `id`
 * and the snake_case spelling; widening it is a separate change, and it is not on this path.)
 */
export const canSellOutOfStock = (userRole: StockCheckRole | null | undefined): boolean =>
  Boolean(userRole?.permissions?.sellOutOfStockProducts)
  || userRole?.id === 'admin'
  || userRole?.firestoreId === 'admin'
  || userRole?.firestore_id === 'admin'

/**
 * Current stock of a product in `store`, read from the live catalog.
 *
 * The live product doc wins whenever the catalog still knows the product — including when it
 * carries no `storeStock` at all, which is a real answer ("no stock recorded here") and must not
 * be papered over with the cart line's stale copy. The cart line is used only when the product has
 * left the catalog, so a mid-cart deletion produces the same outcome as before rather than a
 * refusal the cashier cannot act on.
 */
const liveStoreStock = (
  productsDict: StockCheckProductsDict,
  productId: string | undefined,
  fallback: StockCheckProduct | undefined,
  store: string,
): unknown => {
  const source = (productId ? productsDict?.[productId] : undefined) ?? fallback
  return source?.storeStock?.[store]
}

/**
 * The stock value to compare against, or `null` for "no answer" — which stays permissive.
 *
 * This reproduces the loose `storeStock?.[store] < quantity` comparison the screen has always
 * used, rather than approximating it: `undefined` (no entry for this store) and an unparseable
 * value both compared false and so must stay permissive, while `null` and a numeric string
 * coerced to a number and so must keep being compared. A `typeof === 'number'` test would silently
 * stop enforcing the permission for both of the latter.
 */
const comparableNumber = (raw: unknown): number | null => {
  if (raw === undefined) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

/**
 * The first cart line whose CURRENT stock cannot cover it, or `null` when the whole cart may be
 * sold. Returns `null` immediately for roles allowed to sell out of stock.
 *
 * Line shapes follow the checkout's existing split: a composite assembled AT sale time consumes
 * its ingredients' stock, anything else consumes its own. Requirements accumulate per product as
 * the cart is walked, so the line reported is the one that first pushes a product past its stock —
 * and a product spread over several lines is judged on their sum.
 */
export const findInsufficientStockLine = ({
  cartItems,
  productsDict,
  store,
  userRole,
}: FindInsufficientStockLineArgs): InsufficientStockLine | null => {
  if (canSellOutOfStock(userRole)) return null

  /**
   * Running total of what the cart draws from each product. Keyed by product id; a line with no
   * id cannot be pooled with anything, so it keys on itself and is judged alone, exactly as the
   * per-line check used to judge every line.
   */
  const drawnSoFar = new Map<string | object, number>()

  /**
   * Add a draw on one product and report whether the cart's running total now exceeds its stock.
   * `fallback` is consulted for stock only when the catalog no longer has the product.
   */
  const exceedsStock = (
    key: string | object,
    productId: string | undefined,
    quantity: unknown,
    fallback: StockCheckProduct | undefined,
  ): { available: number, attempted: number } | null => {
    const required = comparableNumber(quantity)
    // An uncomparable quantity contributed nothing to the old per-line comparison either, and
    // must not poison the running total for the lines that follow.
    if (required === null) return null
    const total = (drawnSoFar.get(key) ?? 0) + required
    drawnSoFar.set(key, total)
    const available = comparableNumber(liveStoreStock(productsDict, productId, fallback, store))
    return available !== null && available < total ? { available, attempted: total } : null
  }

  for (const line of cartItems ?? []) {
    // The pseudo lines carry no stock, so they are not part of the cart's draw. `isPseudoLine`
    // also recognises the `lineType` tag, which a saved cart round-tripped through Supabase
    // carries in place of the legacy sentinel id.
    //
    // NOTE the asymmetry, and do not read this skip as "the write path agrees": `saveNewSale`
    // still tests the two raw sentinel ids (sales.ts) rather than `isPseudoLine`, so a
    // `lineType`-tagged pseudo line would reach its `batch.update` and take the whole sale down
    // on a ref that names no product. `deleteComplexSale` was already corrected to
    // `isPseudoLine` for that exact reason; `saveNewSale` has not been, and it is a stock-write
    // change that needs its own ticket and delta-parity test (stockWriteRegistry.ts). Refusing
    // the line here would not help — it is not a stock problem.
    if (isPseudoLine(line)) continue
    // Only lines the sale will actually carry: `submit` drops everything that is not `quantity > 0`
    // before handing the cart to `saveNewSale`, so a zeroed-out line must not refuse a sale it
    // takes no part in — it would name a product the cashier is not selling.
    if (!(Number(line?.quantity) > 0)) continue

    if (!(line?.isComposite && !line.manufacturedBeforeSale)) {
      const productId = line?.id ?? undefined
      const short = exceedsStock(productId || line, productId, line.quantity, line)
      if (short) {
        return { kind: 'product', productName: line?.name, shortProductId: productId ?? null, ...short }
      }
      continue
    }
    for (const ingredient of line.ingredients ?? []) {
      const liveIngredient = productsDict?.[ingredient.id]
      // An ingredient absent from the catalog is skipped here exactly as before; `saveNewSale`
      // is the layer that refuses it, by name, with INGREDIENT_NOT_FOUND.
      if (!liveIngredient) continue
      const required = Number(line.quantity) * Number(ingredient.quantity)
      const short = exceedsStock(ingredient.id, ingredient.id, required, liveIngredient)
      if (short) {
        return {
          kind: 'ingredient',
          ingredientName: liveIngredient.name,
          productName: line?.name,
          shortProductId: ingredient.id,
          ...short,
        }
      }
    }
  }

  return null
}
